import { FC, useState } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardSource,
    getCampaignCreationWizardManualCpcProgress,
    getCampaignCreationWizardStepForSettingsBack,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProcess, CampaignProductType, isFirstStep, STEPS_COUNT_BY_PROCESS } from 'utils/constants/campaign';

import creationWizardPlacementBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_placement_back_button_click';
import creationWizardToCampaignsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_to_campaigns_button_click';
import campaignCreateStepsBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_create_steps_back_button_click';
import campaignEditButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/campaign_edit_button_click';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useCpcNextStep } from 'hooks/useCpcNextStep';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import { CampaignTarget, WizardCpcMode } from 'store/models/campaignCreationWizard';
import { selectCampaignProcess, setCampaignFormStep } from 'store/models/createCampaign';
import { selectIsMainMenuDataExist, selectMainMenuLink, selectMainMenuVacancyId } from 'store/models/mainMenu';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/CpcActions/CpcActions.module.less';

const CAMPAIGN_CREATING_STATUSES = [CampaignProcess.Create, CampaignProcess.Copy];

const sendInternalAnalytics = (campaignProcess: CampaignProcess, currentStep: number, campaignId: number) => {
    if (campaignProcess === CampaignProcess.Create) {
        campaignCreateStepsBackButtonClick({ currentStep });
        return;
    }
    if (campaignProcess === CampaignProcess.Edit) {
        campaignEditButtonClick({ buttonName: 'campaign_edit_step2_back', campaignId });
    }
};

const TrlKeys = {
    save: 'AdCreate.AdForm.save',
    start: 'AdCreate.AdForm.start',
    nextStep: 'AdCreate.AdForm.nextStepMagritte',
    prevStep: 'AdCreate.AdForm.prevStep',
};

export const CpcActions: FC = () => {
    const { currentStep } = useSelector((state) => state.createCampaign);
    const campaignProcess = useSelector(selectCampaignProcess);
    const { campaignId } = useSelector((state) => state.createCampaign);
    const isVideoLoading = useSelector((state) => state.createBanner.isVideoLoading);
    const isMainMenuVacancyExist = useSelector(selectMainMenuVacancyId);
    const isMainMenuDataExist = useSelector(selectIsMainMenuDataExist);
    const mainMenuLink = useSelector(selectMainMenuLink);
    const bannerVacancyId = useSelector((state) => state.createBanner.bannerVacancyId);
    const bannerVacancyName = useSelector((state) => state.createBanner.vacancyName);
    const bannerLink = useSelector((state) => state.createBanner.bannerLink);

    const [isSaving, setSaving] = useState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const dashboardRoute = useDashboardRoute();
    const onNextStepClick = useCpcNextStep(setSaving);

    const formCountSteps = STEPS_COUNT_BY_PROCESS[campaignProcess] - 1;
    const isLastStep = formCountSteps === currentStep;
    const isCampaignCreatingProcess = CAMPAIGN_CREATING_STATUSES.includes(campaignProcess);
    const isLinkFlow = Boolean(mainMenuLink || (bannerLink && !bannerVacancyId));
    const wizardFlowSource =
        isLinkFlow || (!bannerVacancyId && isMainMenuDataExist)
            ? CampaignCreationWizardSource.ExternalLink
            : CampaignCreationWizardSource.VacancyList;
    const isWizardFlowEnabled =
        campaignProcess === CampaignProcess.Create && Boolean(isMainMenuDataExist || bannerVacancyId || isLinkFlow);
    const shouldShowBackButton = isWizardFlowEnabled || !isFirstStep(currentStep);
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const handlePrevStepClick = () => {
        if (!isFirstStep(currentStep)) {
            sendInternalAnalytics(campaignProcess, currentStep, campaignId ?? 0);
            dispatch(setCampaignFormStep(currentStep - 1));
        } else if (isWizardFlowEnabled) {
            creationWizardPlacementBackButtonClick({ placementType: 'CPC_FLEXIBLE' });
            const effectiveVacancyId = bannerVacancyId || isMainMenuVacancyExist;
            const isExternalLinkFlow = wizardFlowSource === CampaignCreationWizardSource.ExternalLink;
            openBackAlert(() =>
                goToWizardStep(getCampaignCreationWizardStepForSettingsBack(wizardFlowSource), {
                    vacancyId: effectiveVacancyId ? Number(effectiveVacancyId) : null,
                    vacancyName: bannerVacancyName ?? undefined,
                    productType: isExternalLinkFlow ? null : CampaignProductType.Cpc,
                    cpcMode: isExternalLinkFlow ? null : WizardCpcMode.Flexible,
                    ...(isLinkFlow &&
                        !effectiveVacancyId && {
                            websiteUrl: mainMenuLink || bannerLink,
                            campaignTarget: CampaignTarget.Website,
                        }),
                })
            );
        } else {
            dispatch(
                isMainMenuVacancyExist ? push(CREATE_MENU_PAGE_ROUTE, { saveMainMenuData: true }) : push(dashboardRoute)
            );
        }
    };

    const handleToAdsClick = () => {
        creationWizardToCampaignsButtonClick();
        if (isWizardFlowEnabled) {
            openBackAlert(() => dispatch(push(dashboardRoute)));
        } else {
            dispatch(push(dashboardRoute));
        }
    };

    const getPrimaryButtonTrl = () => {
        if (isLastStep) {
            return isCampaignCreatingProcess ? TrlKeys.start : TrlKeys.save;
        }
        return TrlKeys.nextStep;
    };

    const wizardProgressConfig = getCampaignCreationWizardManualCpcProgress(currentStep, wizardFlowSource);
    const wizardProgress = (
        <ProgressBar
            progress={wizardProgressConfig.progress}
            segments={wizardProgressConfig.segments}
            data-qa="ad-create-progress-bar"
        />
    );
    const progressConfig = {
        progress: currentStep + 1,
        segments: STEPS_COUNT_BY_PROCESS[campaignProcess],
    } as const;
    const progress = (
        <ProgressBar
            progress={progressConfig.progress}
            segments={progressConfig.segments}
            data-qa="ad-create-progress-bar"
        />
    );

    return (
        <>
            <ActionBar
                type={isMobile ? 'vertical' : 'horizontal'}
                isSticky
                showProgress={isWizardFlowEnabled ? wizardProgress : progress}
                additionalContent={
                    isMobile ? (
                        <div className={styles.progress}>{isWizardFlowEnabled ? wizardProgress : progress}</div>
                    ) : null
                }
                primaryActions={
                    <>
                        {shouldShowBackButton && (
                            <Button onClick={handlePrevStepClick} data-qa="prev-step" mode="secondary" style="accent">
                                {trls(TrlKeys.prevStep)}
                            </Button>
                        )}
                        <Button
                            data-qa={isLastStep ? 'start-campaign' : 'next'}
                            disabled={isSaving || isVideoLoading}
                            loading={isSaving}
                            mode="primary"
                            style="accent"
                            onClick={onNextStepClick}
                        >
                            {trls(getPrimaryButtonTrl())}
                        </Button>
                    </>
                }
                secondaryActions={
                    !isMobile && (
                        <Button onClick={handleToAdsClick} data-qa="cpc-wizard-to-ads" mode="secondary" style="accent">
                            {trls('CampaignCreationWizard.actionBar.toAds')}
                        </Button>
                    )
                }
            />
            {isWizardFlowEnabled && backAlert}
        </>
    );
};
